import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Accordion, { AccordionProps } from "@insite/mobius/Accordion";
import { AccordionSectionPresentationProps, ManagedAccordionSection } from "@insite/mobius/AccordionSection";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { IconMemo, IconPresentationProps, IconProps } from "@insite/mobius/Icon";
import Minus from "@insite/mobius/Icons/Minus";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Plus from "@tIntermatic/Icons/Plus";
import * as React from "react";
import { css } from "styled-components";

const enum fields {
    title = "title",
}
interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
    };
}
type Props = OwnProps & HasProduct;

export interface ProductDetailsDocumentsStyles {
    documentLink?: LinkPresentationProps;
    firmwareLink?: InjectableCss;
    programmingLink?: InjectableCss;
    postSiteMessage?: InjectableCss;
    preSiteMessage?: InjectableCss;
    documentIcon?: IconProps;
    documentNameText?: TypographyPresentationProps;
    wrapper?: InjectableCss;
    toggleIconProps?: IconPresentationProps;
    specificationsAccordionSection?: AccordionSectionPresentationProps;
}

export const documentsStyles: ProductDetailsDocumentsStyles = {
    wrapper: {
        css: css`
            border-bottom: 1px solid rgba(0, 0, 0, 0.1);
        `,
    },
    specificationsAccordionSection: {
        toggleIconProps: {
            src: Minus,
            size: 15,
        },

        expandIconProps: {
            src: Plus,
            size: 15,
        },

        headerProps: {
            css: css`
                > button {
                    background: #fff;
                    border: none;
                    padding: 13px 9px 13px 15px !important;
                    > span {
                        font-size: 18px;
                        font-weight: 600;
                        letter-spacing: 0;
                        line-height: 22px;
                        color: #333333;
                        text-transform: capitalize;
                    }
                }
            `,
        },

        panelProps: {
            css: css`
                border: none;
                padding-top: 0px;
                font-size: 16px;
                font-weight: 300;
                letter-spacing: 0;
                line-height: 24px;
                color: #33333a;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        null,
                        null,
                        null,
                        css`
                            padding-bottom: 7px;
                        `,
                        css`
                            padding-bottom: 7px;
                        `,
                    ])}
            `,
        },
    },

    firmwareLink:{
        css: css`
            display: flex;
            vertical-align: middle;
            ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(
                            theme,
                            [   
                                null,
                                null,
                                css`
                                    font-size: 13px;
                                    display: block;
                                `,
                                null,
                                null,
                            ],
                            "max",
                        )}
            a {
                padding-left: 4px;
                padding-top: 2px;
                padding-right: 4px;
                ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(
                            theme,
                            [   null,
                                null,
                                css`
                                    span {
                                        font-size: 13px;
                                        display: flex;
                                    }
                                `,
                                null,
                                null,
                            ],
                            "max",
                        )}
            }
        `,

    },

    postSiteMessage:{
        css: css`
            padding-left: 4px;
        `,
    },

    programmingLink:{
        css: css`
            display: flex;
            vertical-align: middle;
            a {
                padding-top: 2px;
                ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(
                            theme,
                            [   null,
                                null,
                                css`
                                    span {
                                        font-size: 16px;
                                        display: flex;
                                    }
                                `,
                                null,
                                null,
                            ],
                            "max",
                        )}
            }
        `,

    },

    documentNameText: {
        css: css`
            vertical-align: super;
            font-size: 16px;
            font-weight: 300;
            letter-spacing: 0;
            line-height: 20px;
            color: #006bc0;
        `,
    },
};

const styles = documentsStyles;

const ProductDetailsDocuments: React.FC<Props> = ({ product, fields }) => {
    if ((!product.documents || product.documents?.length === 0) && 
        !product.properties?.firmwareDownloadURL && !product.properties?.programmingGuideURL) {
        return null;
    }
    const limitedDocuments = product.documents?.filter(document => {
        return document.documentType.toLowerCase() === "document";
    });

    if (limitedDocuments?.length === 0) {
        return null;
    }

    return (
        <StyledWrapper>
            <Accordion headingLevel={1} {...styles.wrapper}>
                <ManagedAccordionSection
                    title={fields.title}
                    initialExpanded={false}
                    {...styles.specificationsAccordionSection}
                >
                    {limitedDocuments?.map(document => (
                        <>
                            <StyledWrapper key={document.id}>
                                <Link
                                    href={document.filePath}
                                    target="_blank"
                                    {...styles.documentLink}
                                    data-test-selector={`productDetails_${document.id}`}
                                >
                                    <Typography {...styles.documentNameText}>{document.description}</Typography>
                                </Link>
                            </StyledWrapper>
                        </>
                    ))}
                    {product.properties?.programmingGuideURL && (
                        <StyledWrapper key={product.id}{...styles.programmingLink}>
                            <Link
                                href={product.properties?.programmingGuideURL}
                                target="_blank"
                                data-test-selector={`productDetails_${product.id}pgURL`}
                            >
                                <Typography {...styles.documentNameText}>{siteMessage("programmingGuideURLMessage")}</Typography>
                            </Link>
                        </StyledWrapper>
                    )}
                    {product.properties?.firmwareDownloadURL && (
                        <StyledWrapper key={product.id}{...styles.firmwareLink}>
                            {siteMessage("PreFirmwareLinkMessage")}
                            <Link
                                href={product.properties?.firmwareDownloadURL}
                                target="_blank"
                                data-test-selector={`productDetails_${product.id}fdURL`}
                            >
                                <Typography {...styles.documentNameText}>{siteMessage("FirmWareLinkMessage")}</Typography>
                            </Link>
                            {siteMessage("PostFirmwareLinkMessage")}
                        </StyledWrapper>
                    )}
                </ManagedAccordionSection>
            </Accordion>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(ProductDetailsDocuments),
    definition: {
        displayName: "Documents",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "Documents & Downloads",
                fieldType: "Translatable",
                isRequired: true,
            },
        ],
    },
};

export default widgetModule;
